import { create } from 'zustand';

/**
 * Auth state — the bearer token the API client attaches to every request +
 * the resolved caller envelope when known. The token is persisted in
 * localStorage under `caliber.token` so reloads stay logged in; the
 * `subscribeWithBroadcast` flavor wires cross-tab logout for free in a
 * later iteration (out of scope for this scaffold).
 *
 * Two production auth paths land on this same store:
 *
 *   - **Entra ID firm-team login** (T-0010). Production wires an MSAL
 *     PublicClientApplication; the redirect handler stores the access
 *     token here. Not implemented in this scaffold — see README for the
 *     dev-admin shortcut.
 *
 *   - **Client-viewer magic-link redeem** (T-0011). POST
 *     `/api/v1/auth/magic-link/redeem` with the token from the email
 *     link; the response carries the session details. Wired through
 *     `redeemMagicLink` below; the resulting grant-id (NOT a JWT) is
 *     stored separately for the chat surface which sends it via
 *     `X-Viewer-Grant-Id`.
 */

const TOKEN_KEY = 'caliber.token';
const USER_ID_KEY = 'caliber.user_id';
const VIEWER_GRANT_KEY = 'caliber.viewer_grant_id';

/**
 * The seeded firm-admin user id from `db/prisma/seed.ts`. Used by the
 * dev-auth shortcut (gap #4 from the backend-gaps-for-web fix) so the
 * local API + web UI handshake works without real Entra ID wiring.
 */
const DEV_ADMIN_USER_ID = '00000000-0000-0000-0000-000000000001';
const DEV_ADMIN_EMAIL = 'dev@example.test';

/**
 * Whether the dev-auth shortcut should auto-populate an admin session on
 * page load. Two inputs:
 *
 *   - `VITE_DEV_AUTH` env var ("0" / "1") — explicit override; preferred.
 *   - Hostname — defaults to true on localhost / 127.0.0.1, false elsewhere.
 *
 * The runtime check is intentionally weak: this is a local-dev convenience,
 * not a security boundary. The matching API-side gate (`CALIBER_DEV_AUTH=1`
 * on the Fastify host) is the actual boundary — the API rejects any
 * `Bearer user:<uuid>` token when the flag isn't set.
 */
function isDevAuthShortcutEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const explicit = import.meta.env.VITE_DEV_AUTH;
  if (explicit === '0' || explicit === 'false') return false;
  if (explicit === '1' || explicit === 'true') return true;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * Token used by the dev-auth shortcut. The Fastify host's
 * `createDevJwtVerifier` (apps/api/src/lib/dev-auth.ts) accepts any
 * `Bearer user:<uuid>` token; production is locked-down.
 */
function devAdminBearerToken(): string {
  return `user:${DEV_ADMIN_USER_ID}`;
}

export interface AuthIdentity {
  /** Internal users.id (UUID). Set by both the dev-admin shortcut and the real Entra flow. */
  userId: string;
  email: string;
  /**
   * Display label for the header. Server-resolved when known; falls back to email.
   */
  displayName: string;
}

interface AuthState {
  token: string | null;
  identity: AuthIdentity | null;
  /** Client-viewer grant id (T-0026). Separate from the firm-team token. */
  viewerGrantId: string | null;
  setSession: (token: string, identity: AuthIdentity) => void;
  setViewerGrant: (grantId: string | null) => void;
  clear: () => void;
}

function readInitialToken(): string | null {
  if (typeof window === 'undefined') return null;
  const existing = window.localStorage.getItem(TOKEN_KEY);
  if (existing) return existing;
  // Dev-auth shortcut: prepopulate a real backend-recognized token so the
  // first page load works without the user clicking through the login
  // screen. Production never satisfies the shortcut check.
  if (isDevAuthShortcutEnabled()) {
    const token = devAdminBearerToken();
    window.localStorage.setItem(TOKEN_KEY, token);
    window.localStorage.setItem(USER_ID_KEY, DEV_ADMIN_USER_ID);
    window.localStorage.setItem(
      'caliber.identity',
      JSON.stringify({ email: DEV_ADMIN_EMAIL, displayName: 'Dev Admin' }),
    );
    return token;
  }
  return null;
}

function readInitialIdentity(): AuthIdentity | null {
  if (typeof window === 'undefined') return null;
  const userId = window.localStorage.getItem(USER_ID_KEY);
  if (!userId) return null;
  // Email + display name are persisted as JSON alongside the user id.
  const raw = window.localStorage.getItem('caliber.identity');
  if (!raw) return { userId, email: '', displayName: '' };
  try {
    const parsed = JSON.parse(raw) as Partial<AuthIdentity>;
    return {
      userId,
      email: typeof parsed.email === 'string' ? parsed.email : '',
      displayName: typeof parsed.displayName === 'string' ? parsed.displayName : '',
    };
  } catch {
    return { userId, email: '', displayName: '' };
  }
}

function readInitialViewerGrant(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(VIEWER_GRANT_KEY);
}

export const useAuth = create<AuthState>((set) => ({
  token: readInitialToken(),
  identity: readInitialIdentity(),
  viewerGrantId: readInitialViewerGrant(),
  setSession: (token, identity) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TOKEN_KEY, token);
      window.localStorage.setItem(USER_ID_KEY, identity.userId);
      window.localStorage.setItem(
        'caliber.identity',
        JSON.stringify({ email: identity.email, displayName: identity.displayName }),
      );
    }
    set({ token, identity });
  },
  setViewerGrant: (grantId) => {
    if (typeof window !== 'undefined') {
      if (grantId) window.localStorage.setItem(VIEWER_GRANT_KEY, grantId);
      else window.localStorage.removeItem(VIEWER_GRANT_KEY);
    }
    set({ viewerGrantId: grantId });
  },
  clear: () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(USER_ID_KEY);
      window.localStorage.removeItem('caliber.identity');
      window.localStorage.removeItem(VIEWER_GRANT_KEY);
    }
    set({ token: null, identity: null, viewerGrantId: null });
  },
}));

/** Non-hook reader for the API client to attach the bearer header. */
export function getAuthToken(): string | null {
  return useAuth.getState().token;
}

/** Non-hook reader for the chat surface's X-Viewer-Grant-Id header. */
export function getViewerGrantId(): string | null {
  return useAuth.getState().viewerGrantId;
}
